/**
 * A question card once it is answered, in the Work panel (docs/2026-10-05-answered-card.md).
 */
import { useState } from "react";
import { z } from "zod";
import type { AnswerChoice } from "../../../skills/leadcue-reply/scripts/lib/answer-scope.ts";
import { appliesTo, reachLabel } from "../src/answer-scope-view.ts";
import { type AnsweredLine, answeredLines } from "../src/answered-view.ts";
import { viewerTimeZone } from "../src/app-dates.ts";
import { cardQuestion } from "../src/card-question.ts";

/** What the answer route reports for each answer also held for every lead (`app.ts`). */
export const StandingOutcomeSchema = z.object({
  key: z.string(),
  saved: z.boolean(),
  error: z.string().optional(),
});
export type StandingOutcome = z.infer<typeof StandingOutcomeSchema>;

/** The scope a saved Yes/No answer carries, or null for any other answer. */
function decidedScope(value: unknown): AnswerChoice | null {
  const parsed = z.object({ scope: z.enum(["thread", "campaign"]) }).safeParse(value);
  return parsed.success ? parsed.data.scope : null;
}
/** Why a Standing answer from a card was not saved, in the rep's terms. */
function standingFailure(error: string | undefined): string {
  switch (error) {
    case "supersede_not_acknowledged":
      return "this campaign already has a Standing answer for it";
    case "not_a_standing_fact":
    case "answer_not_typed":
      return "a Standing answer cannot hold this answer";
    default:
      return "it could not be saved";
  }
}

/** Longer than this, an answer is cut to a few lines until the rep opens it. */
const LONG_ANSWER_CHARACTERS = 320;
const LONG_ANSWER_LINES = 6;

/** A link shown by where it goes, without the scheme a rep never types. */
const linkText = (href: string): string => href.replace(/^https?:\/\//u, "").replace(/\/$/u, "");

function AnsweredValue({ lines }: { lines: readonly AnsweredLine[] | null }) {
  const [open, setOpen] = useState(false);
  // An answer nothing formats says only that it was saved; its stored form is never shown.
  if (lines === null) return <dd className="lcr-answered-value">Answer saved</dd>;
  const only = lines.length === 1 ? lines[0] : undefined;
  if (only && "text" in only) {
    const long =
      only.text.length > LONG_ANSWER_CHARACTERS || only.text.split("\n").length > LONG_ANSWER_LINES;
    return (
      <dd>
        <p className="lcr-answered-value" data-clamped={long && !open ? "" : undefined}>
          {only.text}
        </p>
        {long && (
          <button
            type="button"
            className="lcr-answered-more"
            aria-expanded={open}
            onClick={() => setOpen((current) => !current)}
          >
            {open ? "Show less" : "Show all"}
          </button>
        )}
      </dd>
    );
  }
  const part = (entry: AnsweredLine) =>
    "href" in entry ? (
      <a href={entry.href} target="_blank" rel="noreferrer">
        {linkText(entry.href)}
      </a>
    ) : (
      entry.text
    );
  if (only) return <dd className="lcr-answered-value">{part(only)}</dd>;
  return (
    <dd>
      <ul className="lcr-answered-value">
        {lines.map((entry) => (
          <li key={"href" in entry ? entry.href : entry.text}>{part(entry)}</li>
        ))}
      </ul>
    </dd>
  );
}

/**
 * What was asked, what was answered, how far it was saved and how to change it, in that order.
 * The question keeps the look of the label it had on the form and the answer sits where the field
 * sat, so the card reads as the same card, finished. The state itself is the card's badge.
 */
export function AnsweredCard({
  questions,
  values,
  campaignName,
  standing,
  answersHref,
  timeZone,
}: {
  questions: readonly { key: string; prompt: string }[];
  /** The saved answer evidence, by question key. */
  values: Readonly<Record<string, unknown>>;
  campaignName: string | null;
  standing: readonly StandingOutcome[];
  answersHref: string;
  /** The zone slot times are read in; the viewer's own when not given. */
  timeZone?: string;
}) {
  const zone = timeZone ?? viewerTimeZone();
  return (
    <div className="lcr-answered">
      <dl>
        {questions.map((question) => (
          <div key={question.key}>
            <dt>{cardQuestion(question.key, question.prompt)}</dt>
            <AnsweredValue
              lines={answeredLines(question.key, values[question.key], { timeZone: zone })}
            />
          </div>
        ))}
      </dl>
      {questions.map((question) => (
        <AnsweredReach
          key={question.key}
          questionKey={question.key}
          questionPrompt={question.prompt}
          campaignName={campaignName}
          outcome={standing.find((entry) => entry.key === question.key)}
          decidedScope={decidedScope(values[question.key])}
          answersHref={answersHref}
        />
      ))}
    </div>
  );
}

/**
 * Where a saved answer went, on the answered card. A choice key is always held for this
 * conversation; whether it also reached every lead is known in the session that saved it, and
 * otherwise the rep is pointed to Standing answers, which holds every such answer.
 */
function AnsweredReach({
  questionKey,
  questionPrompt,
  campaignName,
  outcome,
  decidedScope,
  answersHref,
}: {
  questionKey: string;
  /** The reference question's fixed wording defaults to the campaign (`appliesTo`). */
  questionPrompt: string;
  campaignName: string | null;
  outcome: StandingOutcome | undefined;
  /** A Yes/No answer records its own scope, and never becomes a Standing answer. */
  decidedScope: AnswerChoice | null;
  answersHref: string;
}) {
  const rule = appliesTo(questionKey, questionPrompt);
  if (!rule) return null;
  if (decidedScope === "thread")
    return <p className="lcr-applies-fixed">Saved for this conversation.</p>;
  if (decidedScope === "campaign")
    return (
      <p className="lcr-applies-fixed">
        Saved for this conversation and for <strong>{reachLabel("campaign", campaignName)}</strong>.
      </p>
    );
  if (rule.kind === "fixed")
    return (
      <p className="lcr-applies-fixed">
        Applies to <strong>{reachLabel(rule.reach, campaignName)}</strong>.
      </p>
    );
  const everyLead = reachLabel("campaign", campaignName);
  if (outcome?.saved)
    return (
      <p className="lcr-applies-fixed">
        Saved for this conversation and for <strong>{everyLead}</strong>. Change or remove it in{" "}
        <a href={answersHref}>Standing answers</a>.
      </p>
    );
  if (outcome)
    return (
      <p className="lcr-applies-fixed" role="alert">
        Saved for this conversation only. It was not saved for {everyLead} because{" "}
        {standingFailure(outcome.error)}. Add it in <a href={answersHref}>Standing answers</a>.
      </p>
    );
  return (
    <p className="lcr-applies-fixed">
      Saved for this conversation. Answers for {everyLead} are listed in{" "}
      <a href={answersHref}>Standing answers</a>.
    </p>
  );
}
